import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight } from "lucide-react";
import { FEATURE_LINKS } from "../pages/features/links";

// Public "Features" mega-menu (logged-out visitors). A wide panel of icon +
// name + description cards, driven by the single source of truth in
// pages/features/links.js. Signals these are real standalone destinations.
export default function FeaturesMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-sm font-medium hover:text-[#1D4ED8] transition-colors"
        aria-expanded={open}
        data-testid="nav-features"
      >
        Features
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-[560px] max-w-[calc(100vw-2rem)] bg-white border border-border rounded-2xl shadow-xl shadow-[#0F172A]/10 p-3 z-50">
          <div className="grid grid-cols-2 gap-1.5">
            {FEATURE_LINKS.map((f) => {
              if (f.comingSoon) {
                return (
                  <div
                    key={f.key}
                    className="flex items-start gap-3 rounded-xl p-3 opacity-70 cursor-default select-none"
                    title="Coming soon"
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-secondary text-muted-foreground">
                      <f.icon className="w-[18px] h-[18px]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-muted-foreground">{f.label}</span>
                        <span className="font-mono text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: `${f.accent}1A`, color: f.accent }}>Soon</span>
                      </div>
                      <div className="text-xs text-muted-foreground/70 leading-snug">{f.desc}</div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={f.key}
                  to={f.to}
                  onClick={() => setOpen(false)}
                  className="group flex items-start gap-3 rounded-xl p-3 hover:bg-secondary transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${f.accent}14`, color: f.accent }}>
                    <f.icon className="w-[18px] h-[18px]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0F172A] group-hover:text-[#0025D8] transition-colors">{f.label}</div>
                    <div className="text-xs text-muted-foreground leading-snug">{f.desc}</div>
                  </div>
                </Link>
              );
            })}
          </div>
          <Link
            to="/signup?role=candidate"
            onClick={() => setOpen(false)}
            className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-dashed border-border px-3.5 py-2.5 hover:border-[#0025D8]/40 hover:bg-[#0025D8]/5 transition-colors"
          >
            <span className="text-xs text-muted-foreground">All four tools, free to start.</span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0025D8]">Get started <ArrowRight className="w-3.5 h-3.5" /></span>
          </Link>
        </div>
      )}
    </div>
  );
}
